<script setup lang="ts">
  import { useToast } from '@/components/ui/toast/use-toast'
  import type { DocumentStatus } from '@/composables/useDocumentProcessing'
  import { useTranslation } from '@/composables/useTranslation'
  import {
    FILE_INPUT_ACCEPT,
    isFileSupported,
    SUPPORTED_FILE_TYPES_DISPLAY
  } from '@/constants/documentFileTypes'
  import { downloadDocument } from '@/lib/utils'
  import { Upload } from 'lucide-vue-next'
  import { computed, onMounted, onUnmounted, ref, watch } from 'vue'

  const { t } = useTranslation({
    en: {
      DropFileHere: 'Drop your file here or',
      Browse: 'browse',
      MaxFileSize: 'Max file size:',
      UploadingDocument: 'Uploading Document',
      fileTypeNotSupported: `File type not supported. Supported formats: ${SUPPORTED_FILE_TYPES_DISPLAY.join(', ')}.`,
      FileTypeHint: `Supported: ${SUPPORTED_FILE_TYPES_DISPLAY.join(', ')}`,
      DownloadError: 'Download Error',
      NoDocumentAvailable: 'No document available for download',
      NoDownloadUrl: 'No download URL available for this document',
      DownloadDocument: 'Download Document'
    },
    de: {
      DropFileHere: 'Legen Sie Ihre Datei hier ab oder',
      Browse: 'durchsuchen',
      MaxFileSize: 'Max. Dateigröße:',
      UploadingDocument: 'Dokument wird hochgeladen',
      fileTypeNotSupported: `Dateityp nicht unterstützt. Unterstützte Formate: ${SUPPORTED_FILE_TYPES_DISPLAY.join(', ')}.`,
      FileTypeHint: `Unterstützt: ${SUPPORTED_FILE_TYPES_DISPLAY.join(', ')}`,
      DownloadError: 'Download-Fehler',
      NoDocumentAvailable: 'Kein Dokument zum Herunterladen verfügbar',
      NoDownloadUrl: 'Keine Download-URL für dieses Dokument verfügbar',
      DownloadDocument: 'Dokument herunterladen'
    }
  })

  const { toast } = useToast()

  const props = withDefaults(
    defineProps<{
      documentProcessor: any
      showInfo?: boolean
      compact?: boolean
      onUploadComplete?: () => Promise<void>
      showProgressList?: boolean
    }>(),
    {
      showInfo: true,
      compact: false,
      showProgressList: false
    }
  )

  const emit = defineEmits<{
    fileUploaded: [documentId: string]
    filesSelected: [files: File[]]
    uploadComplete: []
  }>()

  const fileInput = ref<HTMLInputElement | null>(null)
  const dropZone = ref<HTMLDivElement | null>(null)

  // Use the provided processor
  const documentProcessor = computed(() => props.documentProcessor)

  // Track documents uploaded during this session
  const currentSessionDocumentIds = ref<Set<string>>(new Set())

  // Track pending files waiting for processor to become available
  const pendingFiles = ref<File[]>([])
  const isWaitingForProcessor = ref(false)

  // Show only documents uploaded during this session
  const documents = computed(() => {
    return documentProcessor.value.documents.filter((doc: any) => {
      return (
        currentSessionDocumentIds.value.has(doc.uuid) ||
        currentSessionDocumentIds.value.has(doc.id || '')
      )
    })
  })

  // Reset session when component unmounts
  onUnmounted(() => {
    currentSessionDocumentIds.value.clear()
  })

  // Reset session when component mounts
  onMounted(() => {
    currentSessionDocumentIds.value.clear()
  })

  // Watch for processor becoming available - if we have pending files, start upload
  watch(
    () => documentProcessor.value,
    (newProcessor, oldProcessor) => {
      // When processor becomes available (transitions from null/undefined to a value)
      // and we have pending files, start the upload
      if (
        newProcessor &&
        !oldProcessor &&
        pendingFiles.value.length > 0 &&
        isWaitingForProcessor.value
      ) {
        const filesToUpload = [...pendingFiles.value]
        pendingFiles.value = []
        isWaitingForProcessor.value = false
        // Start upload with the pending files
        processFiles(filesToUpload)
      }
    }
  )

  // Watch for new documents being added to the processor and track them
  watch(
    () => documentProcessor.value?.documents,
    (newDocs, oldDocs) => {
      if (
        !documentProcessor.value ||
        !oldDocs ||
        !Array.isArray(oldDocs) ||
        !Array.isArray(newDocs)
      )
        return

      // Find newly added documents
      const oldDocIds = new Set(oldDocs.map((doc: any) => doc.uuid))
      const newlyAdded = newDocs.filter((doc: any) => !oldDocIds.has(doc.uuid))

      // Track any new documents that appear (uploads starting)
      newlyAdded.forEach((doc: any) => {
        currentSessionDocumentIds.value.add(doc.uuid)
        if (doc.id) {
          currentSessionDocumentIds.value.add(doc.id)
        }
      })
    },
    { deep: true }
  )

  // === HELPER FUNCTIONS ===

  const getThumbnailUrl = (doc: any) => {
    if (!doc.document?.images?.length) return null
    const thumbnail = doc.document.images.find((img: any) => img.image_preset === 'thumbnail')
    return thumbnail?.url || null
  }

  // === DRAG & DROP HANDLERS ===

  const preventDefaults = (e: Event) => {
    e.preventDefault()
    e.stopPropagation()
  }

  const onDrop = (e: DragEvent) => {
    e.preventDefault()
    const files = e.dataTransfer?.files
    if (files) {
      handleFiles(Array.from(files))
    }
  }

  const onFileInput = (e: Event) => {
    const input = e.target as HTMLInputElement
    if (input.files) {
      handleFiles(Array.from(input.files))
    }
  }

  // === FILE PROCESSING ===

  const validateFile = (file: File): boolean => {
    if (!isFileSupported(file)) {
      toast({
        variant: 'destructive',
        title: t('fileTypeNotSupported')
      })
      return false
    }

    const isValidSize = file.size <= 100 * 1024 * 1024 // Default 100MB
    if (!isValidSize) {
      console.warn(
        `❌ File ${file.name} is too large (${(file.size / (1024 * 1024)).toFixed(2)} MB)`
      )
    }
    return isValidSize
  }

  const handleFiles = async (files: File[]) => {
    // Filter valid files
    const validFiles = files.filter((file) => {
      return validateFile(file)
    })

    if (validFiles.length === 0) {
      console.warn('❌ No valid files to upload')
      return
    }

    // Emit files selected event (this may trigger session creation in global chat)
    emit('filesSelected', validFiles)

    // Check if processor is available
    if (!documentProcessor.value) {
      // Processor not available yet - store files and wait for it via watcher
      pendingFiles.value = validFiles
      isWaitingForProcessor.value = true
      return
    }

    // Processor is available - proceed with upload
    await processFiles(validFiles)
  }

  // Process files for upload (extracted to be reusable when processor becomes available)
  const processFiles = async (files: File[]) => {
    if (!documentProcessor.value) {
      console.error('❌ Document processor not available')
      return
    }

    // Upload each file using the generic document processing module
    const uploadPromises = files.map(async (file) => {
      try {
        const response = await documentProcessor.value.uploadDocument(file)

        // Track the document UUID for this session
        if (response.uuid) {
          currentSessionDocumentIds.value.add(response.uuid)
        }

        // Emit file uploaded event
        emit('fileUploaded', response.uuid)

        return response
      } catch (error) {
        console.error('❌ Upload failed for file:', file.name, error)
        throw error
      }
    })

    // Wait for all uploads to complete
    try {
      await Promise.all(uploadPromises)

      // Emit upload complete event
      emit('uploadComplete')

      // Call parent callback if provided
      if (props.onUploadComplete) {
        await props.onUploadComplete()
      }
    } catch (error) {
      console.error('❌ Some uploads failed:', error)
    }
  }

  const retryUpload = (doc: DocumentStatus) => {
    if (doc.document?.uuid) {
      documentProcessor.value.retryDocumentProcessing(doc.document.uuid)
    }
  }

  const removeFile = (doc: DocumentStatus) => {
    if (doc.uploadInfo?.id) {
      // Remove upload in progress
      documentProcessor.value.removeUpload(doc.uploadInfo.id)
    } else if (doc.document?.uuid) {
      // For completed documents, you might want to implement deletion
    }
  }

  const downloadFile = (doc: DocumentStatus) => {
    // Get the actual document object
    const actualDoc = doc.document

    if (!actualDoc) {
      console.error('No document available for download')
      toast({
        title: t('DownloadError'),
        description: t('NoDocumentAvailable'),
        variant: 'destructive'
      })
      return
    }

    // Try direct URL first
    if (actualDoc.url) {
      downloadDocument(actualDoc.url, actualDoc.filename || 'document')
      return
    }

    console.error('Unable to download document - no URL or access token available', actualDoc)
    toast({
      title: t('DownloadError'),
      description: t('NoDownloadUrl'),
      variant: 'destructive'
    })
  }
</script>

<template>
  <div class="w-full">
    <!-- Upload Area -->
    <div
      ref="dropZone"
      :class="[
        `flex cursor-pointer justify-center rounded-xl border-2 border-dashed border-gray-300 bg-gray-50 p-8
        transition-colors hover:border-gray-400 hover:bg-gray-100`,
        compact ? 'p-4' : 'p-8'
      ]"
      data-cy="document-upload-dropzone"
      @dragover.prevent="preventDefaults"
      @dragenter.prevent="preventDefaults"
      @dragleave.prevent="preventDefaults"
      @drop="onDrop"
      @click="fileInput?.click()"
    >
      <input
        ref="fileInput"
        type="file"
        class="hidden"
        multiple
        :accept="FILE_INPUT_ACCEPT"
        data-cy="document-upload-file-input"
        @change="onFileInput"
      />
      <div class="text-center">
        <Upload :class="['mx-auto text-gray-400', compact ? 'h-8 w-8' : 'h-12 w-12']" />
        <div class="mt-4 flex flex-wrap justify-center text-sm leading-6 text-gray-600">
          <span class="pe-1 font-medium text-gray-800">{{ t('DropFileHere') }}</span>
          <span class="font-semibold text-purple-600 hover:text-purple-700">
            {{ t('Browse') }}
          </span>
        </div>
        <p class="mt-2 text-xs text-gray-500">{{ t('MaxFileSize') }} 100MB</p>
        <p class="mt-1 text-xs text-gray-400">{{ t('FileTypeHint') }}</p>
      </div>
    </div>

    <!-- Upload Progress List (optional) -->
    <div
      v-if="showProgressList && documents.length > 0"
      class="mt-4 space-y-2"
      data-cy="document-upload-progress-list"
    >
      <div
        v-for="doc in documents"
        :key="doc.uuid"
        class="flex items-center gap-4 px-1 py-2"
      >
        <!-- Document thumbnail -->
        <div class="mr-3 flex-shrink-0">
          <div class="h-10 w-10 overflow-hidden rounded border border-gray-100 bg-white">
            <img
              v-if="getThumbnailUrl(doc)"
              :src="getThumbnailUrl(doc)"
              alt="Document thumbnail"
              class="h-full w-full object-cover"
            />
            <svg
              v-else
              class="h-full w-full p-2 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
          </div>
        </div>

        <!-- Document Info -->
        <div class="min-w-0 flex-1">
          <p class="truncate text-sm font-medium text-gray-900">{{ doc.filename }}</p>

          <!-- Progress Bar for uploading -->
          <div
            v-if="doc.isUploading"
            class="mt-1"
            data-cy="document-upload-progress"
          >
            <div class="flex items-center gap-2">
              <div class="relative h-1.5 w-full overflow-hidden rounded-full bg-gray-200">
                <div
                  class="absolute left-0 top-0 h-full rounded-full bg-violet-500 transition-all duration-300"
                  :style="{ width: `${doc.progress}%` }"
                ></div>
              </div>
              <span class="whitespace-nowrap text-xs font-medium text-gray-700">
                {{ Math.round(doc.progress) }}%
              </span>
            </div>
          </div>
        </div>

        <!-- Status and Actions -->
        <div class="flex items-center gap-3">
          <!-- Status Icon -->
          <div
            v-if="doc.isUploading"
            class="flex h-5 w-5 items-center justify-center"
          >
            <svg
              class="h-5 w-5 animate-spin text-violet-500"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                class="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                stroke-width="4"
              ></circle>
              <path
                class="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
          </div>
          <div
            v-else-if="doc.isProcessing"
            class="flex h-5 w-5 items-center justify-center"
          >
            <svg
              class="h-5 w-5 animate-spin text-violet-500"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                class="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                stroke-width="4"
              ></circle>
              <path
                class="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
          </div>
          <div
            v-else-if="doc.isComplete"
            class="flex h-5 w-5 items-center justify-center"
          >
            <svg
              class="h-5 w-5 text-green-500"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fill-rule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                clip-rule="evenodd"
              />
            </svg>
          </div>
          <div
            v-else-if="doc.hasError"
            class="flex h-5 w-5 items-center justify-center"
          >
            <svg
              class="h-5 w-5 text-red-500"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fill-rule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                clip-rule="evenodd"
              />
            </svg>
          </div>

          <!-- Action Buttons -->
          <div class="flex gap-1">
            <!-- Download Button -->
            <button
              v-if="doc.isComplete || (doc.isProcessing && doc.document)"
              class="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md border border-gray-200 bg-white
                shadow-sm hover:bg-gray-50"
              :title="t('DownloadDocument')"
              @click="downloadFile(doc)"
            >
              <svg
                class="h-4 w-4 text-gray-800"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
            </button>

            <!-- Retry Button -->
            <button
              v-if="doc.hasError"
              class="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md border border-gray-200 bg-white
                shadow-sm hover:bg-gray-50"
              @click="retryUpload(doc)"
            >
              <svg
                class="h-4 w-4 text-gray-800"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
            </button>

            <!-- Remove Button - Only show for uploads in progress -->
            <button
              v-if="doc.isUploading"
              class="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md border border-gray-200 bg-white
                shadow-sm hover:bg-gray-50"
              @click="removeFile(doc)"
            >
              <svg
                class="h-4 w-4 text-gray-800"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
